'use client';

import React from 'react';
import { mergeClasses } from 'minimal-shared/utils';
import { useScrollOffsetTop } from 'minimal-shared/hooks';

import { Container } from '@/components/ui';

export type HeaderSectionProps = React.HTMLAttributes<HTMLElement> & {
  disableOffset?: boolean;
  disableElevation?: boolean;
  slots?: {
    leftArea?: React.ReactNode;
    rightArea?: React.ReactNode;
    topArea?: React.ReactNode;
    centerArea?: React.ReactNode;
    bottomArea?: React.ReactNode;
  };
  slotProps?: {
    container?: React.ComponentProps<typeof Container>;
    centerArea?: React.HTMLAttributes<HTMLDivElement>;
  };
};

export function HeaderSection({
  slots,
  slotProps,
  className,
  disableOffset,
  disableElevation,
  ...other
}: HeaderSectionProps) {
  const { offsetTop: isOffset } = useScrollOffsetTop();

  // Determine classes based on props
  const headerClasses = mergeClasses([
    'sticky top-0 z-30 border border-gray-200',
    isOffset && !disableOffset ? 'bg-white/80 backdrop-blur' : '',
    isOffset && !disableElevation ? 'shadow-md' : '',
    className || '',
  ]);

  // Determine container classes based on layoutQuery
  const containerClasses = {
    'relative flex items-center justify-center gap-3': true,
    'text-current': true,
    'h-[var(--layout-header-mobile-height)]': true,
    'lg:h-[var(--layout-header-desktop-height)]': true,
  };

  return (
    <header className={headerClasses} {...other}>
      {slots?.topArea}

      <Container
        className={mergeClasses([
          Object.entries(containerClasses)
            .filter(([_, value]) => value)
            .map(([key]) => key)
            .join(' '),
        ])}
        {...slotProps?.container}
      >
        {slots?.leftArea}

        <div className="flex flex-1 justify-center" {...slotProps?.centerArea}>
          {slots?.centerArea}
        </div>

        {slots?.rightArea}
      </Container>

      {slots?.bottomArea}
    </header>
  );
}
